import get from 'lodash/get';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/toPromise';
import {Observable} from 'rxjs/Observable';
import {AjaxFactory, HttpMethod} from '../../_libs/redux-observable-list';
import {FILE_GATEWAY_API_URL} from '../../config/constants';
import {BASE_REDUX_LIST_OPTIONS} from '../../config/redux-list';
import {getIsOnline} from '../../settings/reducers';


const EXPIRATION_BUFFER = 15000; // disregard any urls expiring within 15 seconds
const URL_CACHE = "FileDomain-URL_CACHE";

export class AwsS3RequestService {
  static SERVICE_NAME = 'awsS3RequestService';

  static $inject = [
    '$ngRedux',
    '$cacheFactory',
  ];

  constructor(
    $ngRedux,
    $cacheFactory,
  ) {
    /** @private */
    this.$ngRedux = $ngRedux;

    // Moved from FileDomain
    /** @private */
    this.urlCache = $cacheFactory.get(URL_CACHE) || $cacheFactory(URL_CACHE);

    /** @private */
    this.fileUrlFactory = new AjaxFactory({
      crossDomain: BASE_REDUX_LIST_OPTIONS.crossDomain,
      method: HttpMethod.GET,
      useBody: false,
      url: request => `${FILE_GATEWAY_API_URL}/file-gateway/files/${request.fileId}`,
      getToken: BASE_REDUX_LIST_OPTIONS.getToken,
      responseType: AjaxFactory.jsonResponseType,
    });

    /** @private */
    this.filePreviewUrlFactory = new AjaxFactory({
      crossDomain: BASE_REDUX_LIST_OPTIONS.crossDomain,
      method: HttpMethod.GET,
      useBody: false,
      url: request => `${FILE_GATEWAY_API_URL}/file-gateway/files/${request.fileId}/preview${getPreviewQueryParams(request)}`,
      getToken: BASE_REDUX_LIST_OPTIONS.getToken,
      responseType: AjaxFactory.jsonResponseType,
    });

    /** @private */
    this.requestUploadUrlFactory = new AjaxFactory({
      crossDomain: BASE_REDUX_LIST_OPTIONS.crossDomain,
      method: HttpMethod.POST,
      useBody: true,
      contentType: 'json',
      url: `${FILE_GATEWAY_API_URL}/file-gateway/request-upload`,
      getToken: BASE_REDUX_LIST_OPTIONS.getToken,
      responseType: AjaxFactory.jsonResponseType,
    });

    /** @private */
    this.reportUploadSuccessFactory = new AjaxFactory({
      crossDomain: BASE_REDUX_LIST_OPTIONS.crossDomain,
      method: HttpMethod.POST,
      useBody: true,
      contentType: 'json',
      url: `${FILE_GATEWAY_API_URL}/file-gateway/upload-success`,
      getToken: BASE_REDUX_LIST_OPTIONS.getToken,
      responseType: AjaxFactory.jsonResponseType,
    });

    /** @private */
    this.reportUploadErrorFactory = new AjaxFactory({
      crossDomain: BASE_REDUX_LIST_OPTIONS.crossDomain,
      method: HttpMethod.POST,
      useBody: true,
      contentType: 'json',
      url: `${FILE_GATEWAY_API_URL}/file-gateway/upload-error`,
      getToken: BASE_REDUX_LIST_OPTIONS.getToken,
      responseType: AjaxFactory.jsonResponseType,
    });

    /** @private */
    this.requestFilestackPickFactory = new AjaxFactory({
      crossDomain: BASE_REDUX_LIST_OPTIONS.crossDomain,
      method: HttpMethod.POST,
      useBody: true,
      contentType: 'json',
      url: `${FILE_GATEWAY_API_URL}/file-gateway/file-picker/pick`,
      getToken: BASE_REDUX_LIST_OPTIONS.getToken,
      responseType: AjaxFactory.jsonResponseType,
    });

    /** @private */
    this.requestFilestackStoreFactory = new AjaxFactory({
      crossDomain: BASE_REDUX_LIST_OPTIONS.crossDomain,
      method: HttpMethod.POST,
      useBody: true,
      contentType: 'json',
      url: `${FILE_GATEWAY_API_URL}/file-gateway/file-picker/store`,
      getToken: BASE_REDUX_LIST_OPTIONS.getToken,
      responseType: AjaxFactory.jsonResponseType,
    });

    /** @private */
    this.reportFilestackStoreSuccessFactory = new AjaxFactory({
      crossDomain: BASE_REDUX_LIST_OPTIONS.crossDomain,
      method: HttpMethod.POST,
      useBody: false,
      url: request => `${FILE_GATEWAY_API_URL}/file-gateway/file-picker/store-success/${request.fileId}`,
      getToken: BASE_REDUX_LIST_OPTIONS.getToken,
      responseType: AjaxFactory.jsonResponseType,
    });
  }

  /**
   * Fetch a valid pre-signed S3 access GET url for the corresponding file metadata ID.
   * @param {String} fileId
   * @return {Promise<String>}
   */
  getFileUrl(fileId) {
    if (this.urlCache.get(fileId) && this.urlCache.get(fileId).expires > (new Date().getTime() - EXPIRATION_BUFFER)) {
      return Promise.resolve(this.urlCache.get(fileId));

    } else {
      let errorCount = 0;
      const rootState = this.$ngRedux.getState();

      return this.fileUrlFactory
        .sendRequest({
          fileId,
        }, rootState)
        .map(ajaxResponse => ajaxResponse.response)
        .catch((ajaxError, source$) => {
          if (shouldReturnNull(ajaxError)) {
            return Observable.of(null);
          } else {
            return retryIfOnline(
              source$,
              ++errorCount,
              ajaxError,
              getIsOnline(this.$ngRedux.getState()),
            );
          }
        })
        .map(result => {
          if (result && result.url) {
            this.urlCache.put(fileId, result);
            return result.url;
          } else {
            return null;
          }
        })
        .toPromise();
    }
  }

  /**
   * Fetch a valid pre-signed S3 access GET url for the corresponding file metadata ID's specified preview size.
   * @param {String} fileId File ID.
   * @param {String} type Pass 'crop' for a thumbnail, nothing otherwise.
   * @param {String} previewId The size based preview ID.
   * @return {Promise<String>}
   */
  getFilePreviewUrl(fileId, type, previewId) {
    let errorCount = 0;

    return retry201(() => this.filePreviewUrlFactory
      .sendRequest({
        fileId,
        type: type === 'crop' ? 'tb' : 'pv',
        preview: previewId
      }, this.$ngRedux.getState())
      .catch((ajaxError, source$) => {
        if (shouldReturnNull(ajaxError)) {
          return Observable.of({
            response: null,
          });
        } else {
          return retryIfOnline(
            source$,
            errorCount,
            ajaxError,
            getIsOnline(this.$ngRedux.getState()),
          );
        }
      }))
      .map(ajaxResponse => get(ajaxResponse.response, 'url', null))
      .toPromise();
  }

  /**
   * Request a valid pre-signed S3 access PUT|POST url for direct upload to S3.
   * @param {Object} body
   * @return {Promise<Object>}
   */
  requestUploadUrl(body) {
    return this.sendRequestForFactory(this.requestUploadUrlFactory, body);
  }

  /* TODO: technically, if we get a network error at this point, we should update the upload to record that the
   * only remaining action here is to notify the server of the upload success, rather than repeating the whole upload.
   */
  /**
   * Notify the server that the upload was successful.
   * @param {Object} body
   * @return {Promise<Object>}
   */
  reportUploadSuccess(body) {
    return this.sendRequestForFactory(
      this.reportUploadSuccessFactory,
      body,
      ajaxError => ajaxError.status !== 404 && shouldReturnNull(ajaxError),
    );
  }

  /**
   * Notify the server that the upload failed.
   * @param {Object} body
   * @return {Promise<Object>}
   */
  reportUploadError(body) {
    return this.sendRequestForFactory(
      this.reportUploadErrorFactory,
      body,
      ajaxError => ajaxError.status !== 404 && shouldReturnNull(ajaxError),
    );
  }

  requestFilestackPick(body) {
    return this.sendRequestForFactory(this.requestFilestackPickFactory, body);
  }

  requestFilestackStore(body) {
    return this.sendRequestForFactory(this.requestFilestackStoreFactory, body);
  }

  reportFilestackStoreSuccess(body) {
    let errorCount = 0;

    return retry201(() => this.reportFilestackStoreSuccessFactory
      .sendRequest(body, this.$ngRedux.getState())
      .catch((ajaxError, source$) => {
        if (shouldReturnNull(ajaxError)) {
          // When uploading, this is an unrecoverable error.
          return Observable.throw(ajaxError);
        } else {
          return retryIfOnline(
            source$,
            ++errorCount,
            ajaxError,
            getIsOnline(this.$ngRedux.getState()),
          );
        }
      }))
      .map(ajaxResponse => ajaxResponse.response)
      .toPromise();
  }

  /**
   * @param {AjaxFactory} factory
   * @param {Object} body
   * @param {Function} shouldAbort
   * @return {Promise<any> | Promise<T>}
   * @private
   */
  sendRequestForFactory(factory, body, shouldAbort = shouldReturnNull) {
    let errorCount = 0;
    const rootState = this.$ngRedux.getState();

    return factory
      .sendRequest(body, rootState)
      .catch((ajaxError, source$) => {
        if (shouldAbort(ajaxError)) {
          // When uploading, this is an unrecoverable error.
          return Observable.throw(ajaxError);
        } else {
          return retryIfOnline(
            source$,
            ++errorCount,
            ajaxError,
            getIsOnline(this.$ngRedux.getState()),
          );
        }
      })
      .map(ajaxResponse => ajaxResponse.response)
      .toPromise();
  }
}

// If resource doesn't exist, or user doesn't have access to it, stop retrying
function shouldReturnNull(ajaxError) {
  switch (ajaxError.status) {
    case 400:
    case 401:
    case 403:
    case 404:
      return true;
    default:
      return false;
  }
}

function retryIfOnline(source$, errorCount, ajaxError, isOnline) {
  if (!isOnline || errorCount > 5) {
    return Observable.throw(ajaxError);
  } else {
    return applyExpoBackoff(source$, errorCount);
  }
}

// Apply expo-backoff logic if we are online, otherwise, just rethrow the error.
function applyExpoBackoff(source$, errorCount) {
  return Observable
    .timer(errorCount * 5000)
    .switchMap(() => source$);
}

function retry201(fn, retryCount = 0) {
  return fn()
    .switchMap(ajaxResponse => {
      if (ajaxResponse.status !== 201) {
        return Observable.of(ajaxResponse);

      } else if (retryCount < 5) {
        return retry201(fn, retryCount + 1);

      } else {
        return Observable.of({
          response: null,
        });

      }
    });
}

function getPreviewQueryParams(request) {
  return `?type=${request.type === 'crop' ? 'tb' : 'pv'}&preview=${request.previewId}`;
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/aws-api/AwsS3RequestService.js